import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Search, Plus, Archive, ArchiveRestore, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

export default function AdminVenues() {
    const [q, setQ] = useState("");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // venue object or {} for new
    const [managing, setManaging] = useState(null); // venue object for manager dialog
    const load = () => {
        setLoading(true);
        endpoints.adminVenues({ q: q || undefined }).then((d) => setRows(d.venues)).finally(() => setLoading(false));
    };
    useEffect(load, [q]);

    const toggleArchive = async (v) => {
        const next = !v.archived;
        if (!window.confirm(`${next ? "Archive" : "Restore"} "${v.name}"? ${next ? "Existing events remain." : ""}`)) return;
        try { await endpoints.adminUpdateVenue(v.id, { archived: next }); toast.success(next ? "Venue archived" : "Venue restored"); load(); } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="pm-fade" data-testid="admin-venues">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Manage</div>
                    <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Venues</h1>
                </div>
                <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
                    <DialogTrigger asChild>
                        <button onClick={() => setEditing({})} className="pm-btn pm-btn-primary" data-testid="admin-venue-create-btn"><Plus className="w-4 h-4 mr-2" /> Create venue</button>
                    </DialogTrigger>
                    {editing !== null && <VenueDialog venue={editing} onDone={() => { setEditing(null); load(); }} />}
                </Dialog>
            </header>

            <Dialog open={managing !== null} onOpenChange={(o) => !o && setManaging(null)}>
                {managing !== null && <ManagersDialog venue={managing} onDone={() => setManaging(null)} />}
            </Dialog>

            <section className="px-4 md:px-8 py-4 border-b border-[#2a2a2a] sticky top-0 bg-[#0a0a0a] z-10">
                <div className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2">
                    <Search className="w-4 h-4 text-[#666]" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search venue, city, country…" className="flex-1 bg-transparent outline-none text-sm" data-testid="admin-venue-search" />
                </div>
            </section>

            <div className="px-2 md:px-4">
                {loading ? <div className="p-8 font-mono text-xs text-[#666]">Loading…</div> : (
                    <div className="border border-[#2a2a2a] mt-3">
                        {rows.map((v) => (
                            <div key={v.id} className="grid grid-cols-12 gap-3 items-center p-3 border-b border-[#2a2a2a] last:border-b-0 text-sm" data-testid={`admin-venue-row-${v.id}`}>
                                <div className="col-span-12 md:col-span-5">
                                    <div className="font-display font-bold uppercase truncate">{v.name}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666] truncate">{v.city}, {v.country} · cap {v.capacity}</div>
                                </div>
                                <div className="col-span-6 md:col-span-3 text-[10px] font-mono uppercase text-[#a3a3a3]">
                                    {v.event_count} event{v.event_count === 1 ? "" : "s"} · {v.upcoming_event_count} upcoming
                                </div>
                                <div className={`col-span-3 md:col-span-2 text-[10px] font-mono uppercase ${v.archived ? "text-[#ff3333]" : "text-[#00e676]"}`} data-testid={`admin-venue-status-${v.id}`}>
                                    {v.archived ? "Archived" : "Active"}
                                </div>
                                <div className="col-span-12 md:col-span-2 flex gap-1 justify-end flex-wrap">
                                    <button onClick={() => setManaging(v)} className="pm-btn text-[10px]" data-testid={`admin-venue-managers-${v.id}`}>Managers</button>
                                    <button onClick={() => setEditing(v)} className="pm-btn text-[10px]" data-testid={`admin-venue-edit-${v.id}`}><Pencil className="w-3 h-3 mr-1" /> Edit</button>
                                    <button onClick={() => toggleArchive(v)} className="pm-btn text-[10px]" data-testid={`admin-venue-archive-${v.id}`}>
                                        {v.archived ? <><ArchiveRestore className="w-3 h-3 mr-1" /> Restore</> : <><Archive className="w-3 h-3 mr-1" /> Archive</>}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function VenueDialog({ venue, onDone }) {
    const editing = !!venue?.id;
    const [form, setForm] = useState({
        name: venue?.name || "",
        city: venue?.city || "",
        country: venue?.country || "",
        capacity: venue?.capacity ?? 100,
        address: venue?.address || "",
        image_url: venue?.image_url || "",
    });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        if (!form.name.trim() || !form.city.trim() || !form.country.trim()) { toast.error("Name, city, country required"); return; }
        setBusy(true);
        try {
            if (editing) await endpoints.adminUpdateVenue(venue.id, { ...form, capacity: parseInt(form.capacity, 10) || 0 });
            else await endpoints.adminCreateVenue({ ...form, capacity: parseInt(form.capacity, 10) || 0 });
            toast.success(editing ? "Venue updated" : "Venue created");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-lg" data-testid="venue-dialog">
            <DialogHeader><DialogTitle className="font-display uppercase text-2xl">{editing ? "Edit venue" : "Create venue"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
                <TxtField label="Name" v={form.name} on={(x) => setForm({ ...form, name: x })} testId="venue-name-input" />
                <div className="grid grid-cols-2 gap-3">
                    <TxtField label="City" v={form.city} on={(x) => setForm({ ...form, city: x })} testId="venue-city-input" />
                    <TxtField label="Country" v={form.country} on={(x) => setForm({ ...form, country: x })} testId="venue-country-input" />
                </div>
                <TxtField label="Capacity" v={form.capacity} on={(x) => setForm({ ...form, capacity: x })} type="number" testId="venue-capacity-input" />
                <TxtField label="Address" v={form.address} on={(x) => setForm({ ...form, address: x })} />
                <TxtField label="Image URL" v={form.image_url} on={(x) => setForm({ ...form, image_url: x })} />
            </div>
            <DialogFooter>
                <button onClick={submit} disabled={busy} className="pm-btn pm-btn-primary" data-testid="venue-submit">{editing ? "Save" : "Create"}</button>
            </DialogFooter>
        </DialogContent>
    );
}

function TxtField({ label, v, on, testId, type = "text" }) {
    return (
        <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">{label}</span>
            <input type={type} value={v} onChange={(e) => on(e.target.value)} className="w-full mt-1 bg-transparent border-b border-[#2a2a2a] focus:border-[#ff4400] py-2 outline-none" data-testid={testId} />
        </label>
    );
}

function ManagersDialog({ venue, onDone }) {
    const [members, setMembers] = useState([]);
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);
    const load = () => {
        endpoints.adminListVenueMembers(venue.id).then((d) => setMembers(d.members || []));
    };
    useEffect(load, [venue.id]);
    const assign = async () => {
        if (!email) return;
        setBusy(true);
        try { await endpoints.adminAssignVenueManager(venue.id, email); toast.success("Manager assigned"); setEmail(""); load(); }
        catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };
    const remove = async (uid) => {
        if (!window.confirm("Remove this member from the venue?")) return;
        try { await endpoints.adminRemoveVenueMember(venue.id, uid); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    return (
        <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-lg" data-testid="venue-managers-dialog">
            <DialogHeader><DialogTitle className="font-display uppercase text-2xl">{venue.name} · Team</DialogTitle></DialogHeader>
            <div className="space-y-3">
                <div className="flex gap-2 items-end">
                    <label className="flex-1">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#a3a3a3]">Assign manager by email</span>
                        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" className="w-full mt-1 bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none text-sm" data-testid="venue-mgr-email" />
                    </label>
                    <button onClick={assign} disabled={busy || !email} className="pm-btn pm-btn-primary" data-testid="venue-mgr-assign">Assign</button>
                </div>
                <div className="text-[10px] font-mono uppercase text-[#666] pt-2">Members ({members.length})</div>
                <div className="border border-[#2a2a2a]">
                    {members.length === 0 ? <div className="p-3 text-sm text-[#a3a3a3]" data-testid="venue-mgr-empty">No members yet.</div>
                    : members.map((m) => (
                        <div key={m.user_id} className="p-3 border-b border-[#2a2a2a] last:border-b-0 flex items-center gap-3" data-testid={`venue-mgr-row-${m.user_id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-display font-bold uppercase">{m.name}</div>
                                <div className="text-[10px] font-mono uppercase text-[#666]">{m.email}</div>
                            </div>
                            <div className="text-[10px] font-mono uppercase text-[#ff4400]">{m.role}</div>
                            <button onClick={() => remove(m.user_id)} className="pm-btn text-[10px] text-[#ff3333] border-[#ff3333]" data-testid={`venue-mgr-remove-${m.user_id}`}>Remove</button>
                        </div>
                    ))}
                </div>
            </div>
            <DialogFooter><button onClick={onDone} className="pm-btn" data-testid="venue-mgr-done">Done</button></DialogFooter>
        </DialogContent>
    );
}
